/**
 * CoverageDashboardTab.js — v6.32.143 Workstream D
 *
 * Super-Admin live grid showing question-bank coverage by:
 *   Grade × ELD Standard × Key Language Use
 *
 * Drives Sprint 2 content-authoring priorities. Read-only.
 *
 * Design contract: /app/design_guidelines.md (Trust Blue + WIDA scale).
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Layers,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  RefreshCw,
  Target,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cell color tiers — WIDA-proficient palette, not generic.
function cellTone(count) {
  if (count === 0) return { bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200", label: "critical" };
  if (count < 3) return { bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-200", label: "thin" };
  if (count < 5) return { bg: "bg-amber-50", text: "text-amber-800", border: "border-amber-200", label: "low" };
  if (count < 15) return { bg: "bg-emerald-50", text: "text-emerald-800", border: "border-emerald-200", label: "ok" };
  return { bg: "bg-emerald-100", text: "text-emerald-900", border: "border-emerald-300", label: "strong" };
}

export default function CoverageDashboardTab() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [productLine, setProductLine] = useState("practice");

  const fetchCoverage = async (pl = productLine) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/super-admin/coverage?product_line=${pl}`);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load coverage");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoverage(productLine);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productLine]);

  const summary = useMemo(() => {
    if (!data) return null;
    const critical = (data.gaps || []).filter((g) => g.severity === "critical").length;
    const thin = (data.gaps || []).filter((g) => g.severity === "thin").length;
    return { critical, thin, total_gaps: data.gap_count, total_q: data.total_questions };
  }, [data]);

  if (loading) {
    return (
      <Card data-testid="coverage-dashboard-loading">
        <CardContent className="py-12 flex items-center justify-center gap-3 text-slate-600">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Computing WIDA® coverage grid…</span>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card data-testid="coverage-dashboard-error" className="border-rose-300">
        <CardContent className="py-6 text-rose-700">
          <div className="flex items-center gap-2 font-medium">
            <AlertTriangle className="w-5 h-5" />
            Couldn’t load coverage: {error}
          </div>
          <Button
            size="sm"
            variant="outline"
            className="mt-4"
            onClick={() => fetchCoverage(productLine)}
            data-testid="coverage-retry-btn"
          >
            <RefreshCw className="w-4 h-4 mr-1.5" /> Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="coverage-dashboard-root">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-sky-50 text-mosaic-brand flex items-center justify-center">
            <Layers className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-serif text-2xl text-mosaic-ink leading-tight">
              WIDA® Coverage Dashboard
            </h2>
            <p className="text-sm text-mosaic-ink-2">
              Live grid of question-bank coverage by Grade × ELD Standard × Key Language Use.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {["practice", "summative"].map((pl) => (
            <button
              key={pl}
              type="button"
              onClick={() => setProductLine(pl)}
              data-testid={`coverage-product-line-${pl}`}
              className={`text-xs font-medium px-3 py-1.5 rounded-md border transition-colors ${
                productLine === pl
                  ? "bg-mosaic-brand text-white border-mosaic-brand"
                  : "bg-white text-mosaic-ink-2 border-mosaic-border-soft hover:bg-mosaic-bg-subtle"
              }`}
            >
              {pl === "practice" ? "MOSAIC Practice" : "MOSAIC Summative"}
            </button>
          ))}
          <Button
            size="sm"
            variant="outline"
            onClick={() => fetchCoverage(productLine)}
            data-testid="coverage-refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Summary chips */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="coverage-summary">
          <SummaryCard
            label="Total questions"
            value={summary.total_q}
            tone="brand"
            icon={Target}
            testId="coverage-summary-total"
          />
          <SummaryCard
            label="Coverage gaps"
            value={summary.total_gaps}
            tone={summary.total_gaps > 0 ? "amber" : "emerald"}
            icon={AlertTriangle}
            testId="coverage-summary-gaps"
          />
          <SummaryCard
            label="Critical (0)"
            value={summary.critical}
            tone={summary.critical > 0 ? "rose" : "emerald"}
            icon={AlertTriangle}
            testId="coverage-summary-critical"
          />
          <SummaryCard
            label="Thin (<3)"
            value={summary.thin}
            tone={summary.thin > 0 ? "orange" : "emerald"}
            icon={CheckCircle2}
            testId="coverage-summary-thin"
          />
        </div>
      )}

      {/* Per-grade totals strip */}
      <Card data-testid="coverage-per-grade-strip">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-mosaic-ink-2 uppercase tracking-wider">
            Per-grade totals
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 sm:grid-cols-7 gap-2">
            {data.grades.map((g) => {
              const c = data.total_per_grade[g] || 0;
              return (
                <div
                  key={g}
                  className="rounded-md border border-mosaic-border-soft px-3 py-2 text-center"
                  data-testid={`coverage-grade-total-${g}`}
                >
                  <div className="text-xs text-mosaic-ink-3 font-mono">Gr {g}</div>
                  <div className="text-lg font-semibold text-mosaic-ink">{c}</div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* The big grid: Grade rows × (Standard × KLU) columns */}
      <Card data-testid="coverage-grid-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-mosaic-ink-2 uppercase tracking-wider">
            Grade × ELD Standard × Key Language Use
          </CardTitle>
          <p className="text-xs text-mosaic-ink-3 mt-1">
            Each cell shows question count. Red = 0 (critical gap). Orange = thin. Amber = low. Emerald = healthy.
          </p>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="min-w-full text-xs" data-testid="coverage-grid-table">
            <thead>
              <tr>
                <th className="text-left px-3 py-2 font-mono text-mosaic-ink-3 sticky left-0 bg-white z-10">
                  Grade
                </th>
                {data.standards.map((s) => (
                  <th
                    key={s.id}
                    colSpan={data.key_language_uses.length}
                    className="text-center px-2 py-2 font-semibold text-mosaic-ink border-l border-mosaic-border-soft"
                  >
                    {s.short}
                  </th>
                ))}
              </tr>
              <tr>
                <th className="px-3 py-1.5 sticky left-0 bg-white z-10" />
                {data.standards.map((s) =>
                  data.key_language_uses.map((k) => (
                    <th
                      key={`${s.id}-${k.id}`}
                      className="text-center px-1.5 py-1 font-mono text-[10px] text-mosaic-ink-3 font-normal border-l border-mosaic-border-soft/40"
                    >
                      {k.id}
                    </th>
                  ))
                )}
              </tr>
            </thead>
            <tbody>
              {data.grades.map((g) => (
                <tr key={g} className="border-t border-mosaic-border-soft">
                  <td className="px-3 py-1.5 font-mono font-semibold text-mosaic-ink sticky left-0 bg-white z-10">
                    {g}
                  </td>
                  {data.standards.map((s) =>
                    data.key_language_uses.map((k) => {
                      const count = data.by_grade_standard_klu?.[g]?.[String(s.id)]?.[k.id] ?? 0;
                      const tone = cellTone(count);
                      return (
                        <td
                          key={`${g}-${s.id}-${k.id}`}
                          className={`text-center px-2 py-1.5 border-l border-mosaic-border-soft/40 ${tone.bg} ${tone.text}`}
                          title={`Grade ${g} · ${s.short} · ${k.id} → ${count} questions (${tone.label})`}
                          data-testid={`coverage-cell-${g}-${s.id}-${k.id}`}
                        >
                          {count}
                        </td>
                      );
                    })
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Gaps list */}
      {summary && summary.total_gaps > 0 && (
        <Card className="border-amber-200" data-testid="coverage-gaps-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-amber-900 uppercase tracking-wider flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Sprint 2 priority queue — {summary.total_gaps} gap{summary.total_gaps === 1 ? "" : "s"} below 5 questions
            </CardTitle>
            <p className="text-xs text-amber-800 mt-1">
              These cells need new content authored to reach WIDA®-defensible coverage.
              Critical (0) gaps top the queue.
            </p>
          </CardHeader>
          <CardContent>
            <div className="max-h-72 overflow-y-auto divide-y divide-mosaic-border-soft" data-testid="coverage-gaps-list">
              {[...data.gaps]
                .sort((a, b) => a.count - b.count || a.grade.localeCompare(b.grade))
                .slice(0, 60)
                .map((g, i) => {
                  const tone = cellTone(g.count);
                  return (
                    <div
                      key={`${g.grade}-${g.standard}-${g.klu}`}
                      className="py-2 px-1 flex items-center justify-between gap-2"
                      data-testid={`coverage-gap-row-${i}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`inline-flex items-center justify-center w-7 h-7 rounded ${tone.bg} ${tone.text} font-mono text-xs font-semibold`}>
                          {g.count}
                        </span>
                        <div>
                          <div className="text-sm text-mosaic-ink font-medium">
                            Gr {g.grade} · {g.standard_name} · {g.klu}
                          </div>
                          <div className="text-xs text-mosaic-ink-3">
                            Severity: {g.severity}
                          </div>
                        </div>
                      </div>
                      <Badge
                        variant="outline"
                        className={`text-[10px] uppercase tracking-wider ${tone.text} ${tone.border}`}
                      >
                        {tone.label}
                      </Badge>
                    </div>
                  );
                })}
            </div>
            {data.gaps.length > 60 && (
              <p className="text-xs text-mosaic-ink-3 mt-2">
                Showing top 60 of {data.gaps.length} gaps.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Footer disclaimer */}
      <p className="text-xs text-mosaic-ink-3 text-center pt-2">
        MOSAIC is an independent platform. Not affiliated with, endorsed by, or certified by WIDA®.
        Classification uses keyword heuristics; thin cells should be reviewed before Sprint 2 authoring.
      </p>
    </div>
  );
}

function SummaryCard({ label, value, tone, icon: Icon, testId }) {
  const toneMap = {
    brand: "bg-sky-50 text-mosaic-brand border-sky-200",
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    amber: "bg-amber-50 text-amber-900 border-amber-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    orange: "bg-orange-50 text-orange-800 border-orange-200",
  };
  const klass = toneMap[tone] || toneMap.brand;
  return (
    <div
      className={`rounded-lg border ${klass} px-4 py-3 flex items-center gap-3`}
      data-testid={testId}
    >
      <Icon className="w-5 h-5 opacity-70" />
      <div>
        <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
        <div className="text-2xl font-semibold leading-tight">{value}</div>
      </div>
    </div>
  );
}
